/**
 * Field schemas for the Pages CMS admin editor.
 * Each page has an ordered list of sections; each section has a list of fields.
 * Field types: "text" (single line) or "textarea" (multiline).
 *
 * To expose a new editable string on a public page:
 *   1. Add a field here with the desired key
 *   2. Read it on the public page via `copy("the_key", "fallback string in code")`
 *   3. Add a default value in backend/seed_data.py PAGES_DEFAULT
 */
export const PAGE_SCHEMAS = {
  home: {
    label: "Home page",
    sections: [
      {
        title: "Hero",
        fields: [
          { name: "hero_h1_line1", label: "Headline line 1", type: "text" },
          { name: "hero_h1_line2_italic", label: "Headline line 2 (italic)", type: "text" },
          { name: "hero_subcopy", label: "Sub-copy", type: "textarea" },
          { name: "hero_cta_primary", label: "Primary CTA label", type: "text" },
          { name: "hero_cta_secondary", label: "Secondary CTA label", type: "text" },
        ],
      },
      {
        title: "Our Promise (pillars)",
        fields: [
          { name: "pillars_eyebrow", label: "Eyebrow", type: "text" },
          { name: "pillars_h2_part1", label: "Headline part 1", type: "textarea" },
          { name: "pillars_h2_part2_italic", label: "Headline part 2 (italic)", type: "text" },
          { name: "pillars_intro", label: "Intro paragraph", type: "textarea" },
          { name: "pillar_1_title", label: "Pillar 1 title", type: "text" },
          { name: "pillar_1_body", label: "Pillar 1 body", type: "textarea" },
          { name: "pillar_2_title", label: "Pillar 2 title", type: "text" },
          { name: "pillar_2_body", label: "Pillar 2 body", type: "textarea" },
          { name: "pillar_3_title", label: "Pillar 3 title", type: "text" },
          { name: "pillar_3_body", label: "Pillar 3 body", type: "textarea" },
        ],
      },
      {
        title: "Featured destinations strip",
        fields: [
          { name: "destinations_eyebrow", label: "Eyebrow", type: "text" },
          { name: "destinations_h2", label: "Headline", type: "text" },
          { name: "destinations_link_label", label: "Link label", type: "text" },
        ],
      },
      {
        title: "Philosophy",
        fields: [
          { name: "philosophy_image", label: "Philosophy image", type: "image", help: "Square-ish portrait works best (4:5). Leave blank to use the default in code." },
          { name: "philosophy_eyebrow", label: "Eyebrow", type: "text" },
          { name: "philosophy_h2_part1", label: "Headline part 1", type: "text" },
          { name: "philosophy_h2_part2_italic", label: "Headline part 2 (italic)", type: "text" },
          { name: "philosophy_body_1", label: "Body paragraph 1", type: "textarea" },
          { name: "philosophy_body_2", label: "Body paragraph 2", type: "textarea" },
          { name: "philosophy_link_label", label: "Link label", type: "text" },
        ],
      },
      {
        title: "Founder note",
        fields: [
          { name: "founder_image", label: "Founder image", type: "image", help: "Portrait-style (4:5). Leave blank to use the default in code." },
          { name: "founder_eyebrow", label: "Eyebrow", type: "text" },
          { name: "founder_quote", label: "Quote", type: "textarea" },
          { name: "founder_body", label: "Body", type: "textarea" },
          { name: "founder_signature", label: "Signature", type: "text" },
          { name: "founder_signature_subtitle", label: "Signature subtitle", type: "text" },
        ],
      },
      {
        title: "Sample journeys strip",
        fields: [
          { name: "journeys_eyebrow", label: "Eyebrow", type: "text" },
          { name: "journeys_h2_line1", label: "Headline line 1", type: "text" },
          { name: "journeys_h2_line2", label: "Headline line 2", type: "text" },
          { name: "journeys_link_label", label: "Link label", type: "text" },
        ],
      },
      {
        title: "Testimonial section",
        fields: [
          { name: "testimonial_eyebrow", label: "Eyebrow above featured testimonial", type: "text" },
        ],
      },
      {
        title: "Final CTA",
        fields: [
          { name: "cta_eyebrow", label: "Eyebrow", type: "text" },
          { name: "cta_h2_part1", label: "Headline part 1", type: "text" },
          { name: "cta_h2_part2_italic", label: "Headline part 2 (italic)", type: "text" },
          { name: "cta_body", label: "Body", type: "textarea" },
          { name: "cta_label", label: "Button label", type: "text" },
        ],
      },
    ],
  },
  about: {
    label: "About page",
    sections: [
      {
        title: "Hero",
        fields: [
          { name: "hero_image", label: "Hero portrait image", type: "image", help: "Wide aspect ratio. Leave blank to use the default in code." },
          { name: "hero_eyebrow", label: "Eyebrow", type: "text" },
          { name: "hero_h1_part1", label: "Headline part 1", type: "text" },
          { name: "hero_h1_part2_italic", label: "Headline part 2 (italic)", type: "text" },
          { name: "hero_intro", label: "Intro paragraph", type: "textarea" },
        ],
      },
      {
        title: "How we work",
        fields: [
          { name: "howwework_eyebrow", label: "Eyebrow", type: "text" },
          { name: "howwework_quote", label: "Pull quote", type: "textarea" },
          { name: "howwework_body_1", label: "Body paragraph 1", type: "textarea" },
          { name: "howwework_body_2", label: "Body paragraph 2", type: "textarea" },
          { name: "howwework_body_3", label: "Body paragraph 3", type: "textarea" },
        ],
      },
      {
        title: "The Founders",
        fields: [
          { name: "founders_image", label: "Founders image", type: "image", help: "Portrait-style (4:5). Leave blank to use the default in code." },
          { name: "founders_eyebrow", label: "Eyebrow", type: "text" },
          { name: "founders_h2", label: "Headline", type: "text" },
          { name: "founders_body_1", label: "Body paragraph 1", type: "textarea" },
          { name: "founders_body_2", label: "Body paragraph 2", type: "textarea" },
        ],
      },
      {
        title: "The Specialists (heading)",
        fields: [
          { name: "specialists_eyebrow", label: "Eyebrow", type: "text" },
          { name: "specialists_h2", label: "Headline", type: "text" },
          { name: "specialists_intro", label: "Intro paragraph", type: "textarea" },
        ],
      },
    ],
  },
  contact: {
    label: "Contact page",
    sections: [
      {
        title: "Hero",
        fields: [
          { name: "hero_eyebrow", label: "Eyebrow", type: "text" },
          { name: "hero_h1_part1", label: "Headline part 1", type: "text" },
          { name: "hero_h1_part2_italic", label: "Headline part 2 (italic)", type: "text" },
          { name: "hero_intro", label: "Intro paragraph", type: "textarea" },
        ],
      },
      {
        title: "Success state (after submit)",
        fields: [
          { name: "success_eyebrow", label: "Eyebrow", type: "text" },
          { name: "success_h2", label: "Headline", type: "text" },
          { name: "success_body", label: "Body", type: "textarea" },
        ],
      },
    ],
  },
};
